import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import isEmail from 'validator/lib/isEmail';
import useMount from 'react-use/lib/useMount';
import qs from 'qs';
import { isEmpty } from 'lodash-es';

import { Alert, Button, Input } from 'chaoskit/src/components';

const WebinarForm = ({ formId, currentUrl, onDemand, ...props }) => {
  const [errors, setErrors] = useState({});
  const [inputs, setInputs] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [response, setResponse] = useState();

  useMount(() => {
    if (document.referrer) setInputs({ referrer: document.referrer });

    // If we have query params, add them to inputs that will be sent to Netlify
    const params = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

    if (!isEmpty(params))
      setInputs(currentInputs => ({ ...currentInputs, ...params }));
  });

  const handleChange = (name, value) => {
    setInputs(currentInputs => ({ ...currentInputs, [name]: value }));
  };

  const encode = data =>
    Object.keys(data)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
      .join('&');

  const validateForm = () => {
    const formErrors = {};

    if (!inputs.firstName) formErrors.firstName = 'First name is required';
    if (!inputs.lastName) formErrors.lastName = 'Last name is required';
    if (!inputs.company) formErrors.company = 'Company is required';
    if (!inputs.jobTitle) formErrors.jobTitle = 'Job title is required';

    if (!inputs.email || (inputs.email && !isEmail(inputs.email)))
      formErrors.email = 'Valid email is required';

    return formErrors;
  };

  const handleFormSubmission = () => {
    const formErrors = validateForm();

    if (!isEmpty(formErrors)) {
      setErrors(formErrors);

      return;
    }

    // Otherwise clear errors and submit form
    setErrors({});
    setLoading(true);

    fetch(currentUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({ 'form-name': formId, ...inputs }),
    })
      .then(() => {
        if (window.fbq) {
          window.fbq('track', 'Lead');
        }

        if (window.clearbit) {
          window.clearbit.identify(inputs.email, { email: inputs.email });
        }

        setLoading(false);
        setResponse('success');
      })
      .catch(() => {
        setLoading(false);
        setResponse('error');
      });
  };

  return (
    <Fragment>
      {response === 'success' && (
        <Alert type="primary">
          {onDemand ? (
            <p>
              Thank you for registering for this on-demand webinar. You will
              receive an email momentarily with a link to watch the session.
            </p>
          ) : (
            <p>
              <strong>You&apos;re all set!</strong> Thanks for registering for
              our upcoming webinar. Within a few minutes, you will receive an
              email with login information from our webinar provider, BigMarker.
            </p>
          )}
        </Alert>
      )}

      {response === 'error' && (
        <Alert type="error">
          There was an error submitting your registration. Please try again
          later.
        </Alert>
      )}
      {!response && (
        <form
          name={formId}
          method="post"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          {...props}
        >
          <input type="hidden" name="form-name" value={formId} />
          <input
            name="bot-field"
            className="u-hidden"
            onChange={e => handleChange(e.target.name, e.target.value)}
          />
          <Input
            required
            onChange={handleChange}
            validationMessage={errors.email}
            label="Work Email"
            name="email"
            type="email"
          />
          <Input
            required
            onChange={handleChange}
            validationMessage={errors.firstName}
            label="First Name"
            name="firstName"
          />
          <Input
            required
            onChange={handleChange}
            validationMessage={errors.lastName}
            label="Last Name"
            name="lastName"
          />
          <Input
            required
            onChange={handleChange}
            validationMessage={errors.company}
            label="Company"
            name="company"
          />
          <Input
            required
            onChange={handleChange}
            validationMessage={errors.jobTitle}
            label="Job title"
            name="jobTitle"
          />
          <Button
            loading={isLoading}
            onClick={handleFormSubmission}
            type="primary"
            className="u-mt--large"
          >
            Register
          </Button>
        </form>
      )}
    </Fragment>
  );
};

WebinarForm.propTypes = {
  formId: PropTypes.string.isRequired,
  currentUrl: PropTypes.string.isRequired,
  onDemand: PropTypes.bool,
};

export default WebinarForm;
